export type Language = 'en' | 'es' | 'hi' | 'pa' | 'bn';

export interface LanguageInfo {
  code: Language;
  name: string;
  nativeName: string;
}

export const SUPPORTED_LANGUAGES: LanguageInfo[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা' },
];

export interface TriageTranslations {
  intro: {
    title: string;
    subtitle: string;
    howItWorks: string;
    step1: string;
    step2: string;
    step3: string;
    guidance: string;
    homeCare: string;
    seeDoctor: string;
    urgentCare: string;
    emergencyRoom: string;
    disclaimer: string;
    startAssessment: string;
    skipIntro: string;
  };
  wizard: {
    stepTitles: {
      chiefComplaint: string;
      symptomDetails: string;
      vitalsRisk: string;
      emergencySymptoms: string;
      review: string;
    };
    stepDescriptions: {
      chiefComplaint: string;
      symptomDetails: string;
      vitalsRisk: string;
      emergencySymptoms: string;
      review: string;
    };
    navigation: {
      previous: string;
      next: string;
      submit: string;
      saving: string;
      submitting: string;
      completeAssessment: string;
      exit: string;
    };
    exitDialog: {
      title: string;
      description: string;
      continueAssessment: string;
      leaveWithoutSaving: string;
      saveAndExit: string;
    };
    header: {
      title: string;
      stepOf: string;
    };
  };
  chiefComplaint: {
    title: string;
    subtitle: string;
    commonComplaints: string;
    orDescribe: string;
    placeholder: string;
    hint: string;
    complaints: {
      chestPain: string;
      shortnessOfBreath: string;
      headache: string;
      abdominalPain: string;
      fever: string;
      cough: string;
      nauseaVomiting: string;
      dizziness: string;
      backPain: string;
      fatigue: string;
      woundInjury: string;
      rashSkin: string;
    };
  };
  symptomDetails: {
    title: string;
    painSeverity: string;
    painScale: { noPain: string; worstPain: string };
    painLabels: { noPain: string; mild: string; moderate: string; severe: string; worst: string };
    symptomOnset: string;
    onsetPlaceholder: string;
    onsetOptions: {
      lessThan1Hour: string;
      oneToSixHours: string;
      sixTo24Hours: string;
      oneToThreeDays: string;
      threeToSevenDays: string;
      moreThanWeek: string;
    };
    locationTitle: string;
    locationPlaceholder: string;
    symptoms: {
      chestPain: string;
      shortnessOfBreath: string;
      difficultyBreathing: string;
      severeBleeding: string;
      lossOfConsciousness: string;
      confusion: string;
      speechDifficulty: string;
      fever: string;
      severeHeadache: string;
      abdominalPain: string;
      nauseaVomiting: string;
      dizziness: string;
      fatigue: string;
      backPain: string;
      jointPain: string;
    };
  };
  vitalsRisk: {
    prefilledNote: string;
    updateProfile: string;
    vitalsTitle: string;
    temperature: string;
    bloodPressure: string;
    heartRate: string;
    medicalHistoryTitle: string;
    conditions: {
      heartDisease: string;
      hypertension: string;
      diabetes: string;
      asthma: string;
      copd: string;
      chronicKidneyDisease: string;
      liverDisease: string;
      cancer: string;
      immunocompromised: string;
      strokeHistory: string;
      seizureDisorder: string;
      mentalHealthCondition: string;
    };
    medicationsTitle: string;
    medicationsPlaceholder: string;
    medicationsHint: string;
    allergiesTitle: string;
    allergiesPlaceholder: string;
    allergiesHint: string;
    recentVisitsTitle: string;
    visits: {
      emergency: string;
      urgentCare: string;
      primaryCare: string;
      specialist: string;
      hospitalization: string;
    };
    pregnancyLabel: string;
  };
  redFlags: {
    title: string;
    subtitle: string;
    emergencyAlert: string;
    criticalNote: string;
    noEmergencyMessage: string;
    disclaimer: string;
    symptoms: {
      severeBleeding: string;
      unconscious: string;
      difficultyBreathingSevere: string;
      chestPainSevere: string;
      neurological: string;
      severeInjury: string;
      allergicReaction: string;
      suicidalThoughts: string;
      highFever: string;
      severeAbdominalPain: string;
      severeHeadache: string;
      visionChanges: string;
    };
  };
  review: {
    title: string;
    subtitle: string;
    sectionsCompleted: string;
    editHint: string;
    emergencyAlert: string;
    step1Title: string;
    step2Title: string;
    step3Title: string;
    step4Title: string;
    notSpecified: string;
    selectedSymptoms: string;
    noSymptomsSelected: string;
    painLevel: string;
    mild: string;
    moderate: string;
    severe: string;
    onset: string;
    location: string;
    vitalSigns: string;
    temp: string;
    bp: string;
    bpm: string;
    medicalConditions: string;
    currentMedications: string;
    allergies: string;
    pregnant: string;
    noVitalsProvided: string;
    noEmergencySigns: string;
    disclaimer: string;
  };
}

export const translations: Record<Language, TriageTranslations> = {
  en: {
    intro: {
      title: "AI Symptom Checker",
      subtitle: "Get personalized healthcare guidance based on your symptoms",
      howItWorks: "How it works:",
      step1: "Answer a few questions about your symptoms and how you're feeling",
      step2: "Takes only 2-3 minutes to complete the assessment",
      step3: "Your information is secure and HIPAA compliant",
      guidance: "You'll receive guidance on:",
      homeCare: "Home Care",
      seeDoctor: "See Your Doctor",
      urgentCare: "Urgent Care",
      emergencyRoom: "Emergency Room",
      disclaimer: "This tool provides guidance only and is not a substitute for professional medical advice. If you're experiencing a medical emergency, call 911 immediately.",
      startAssessment: "Start Assessment",
      skipIntro: "Skip intro next time",
    },
    wizard: {
      stepTitles: {
        chiefComplaint: "Chief Complaint",
        symptomDetails: "Symptom Details",
        vitalsRisk: "Vitals & Risk Factors",
        emergencySymptoms: "Emergency Symptoms",
        review: "Review",
      },
      stepDescriptions: {
        chiefComplaint: "Tell us what's bothering you the most right now",
        symptomDetails: "Describe your symptoms, severity, and when they started",
        vitalsRisk: "Share any vitals and medical history that may be relevant",
        emergencySymptoms: "Check for any warning signs that need immediate attention",
        review: "Review your answers and get your personalized recommendation",
      },
      navigation: {
        previous: "Previous",
        next: "Next",
        submit: "Get Recommendation",
        saving: "Saving...",
        submitting: "Processing...",
        completeAssessment: "Complete Assessment",
        exit: "Exit",
      },
      exitDialog: {
        title: "Leave Assessment?",
        description: "You have unsaved progress in your health assessment. Would you like to save your progress before leaving?",
        continueAssessment: "Continue Assessment",
        leaveWithoutSaving: "Leave Without Saving",
        saveAndExit: "Save & Exit",
      },
      header: {
        title: "Health Assessment",
        stepOf: "Step {current} of {total}",
      },
    },
    chiefComplaint: {
      title: "What brings you in today? What's your main concern?",
      subtitle: "Select a common complaint below or describe your concern in your own words.",
      commonComplaints: "Common complaints:",
      orDescribe: "Or describe in your own words:",
      placeholder: "Please describe your main symptom or concern...",
      hint: "Be as specific as possible. Include when it started and what makes it better or worse.",
      complaints: {
        chestPain: "Chest pain",
        shortnessOfBreath: "Shortness of breath",
        headache: "Headache",
        abdominalPain: "Abdominal pain",
        fever: "Fever",
        cough: "Cough",
        nauseaVomiting: "Nausea/Vomiting",
        dizziness: "Dizziness",
        backPain: "Back pain",
        fatigue: "Fatigue",
        woundInjury: "Wound/Injury",
        rashSkin: "Rash/Skin issue",
      },
    },
    symptomDetails: {
      title: "Which symptoms are you experiencing? (Select all that apply)",
      painSeverity: "Pain Severity (0 = No pain, 10 = Worst possible pain)",
      painScale: { noPain: "No pain", worstPain: "Worst pain" },
      painLabels: { noPain: "No pain", mild: "Mild pain", moderate: "Moderate pain", severe: "Severe pain", worst: "Worst possible pain" },
      symptomOnset: "When did your symptoms start?",
      onsetPlaceholder: "Choose when symptoms started",
      onsetOptions: {
        lessThan1Hour: "Less than 1 hour ago",
        oneToSixHours: "1-6 hours ago",
        sixTo24Hours: "6-24 hours ago",
        oneToThreeDays: "1-3 days ago",
        threeToSevenDays: "3-7 days ago",
        moreThanWeek: "More than 1 week ago",
      },
      locationTitle: "Where specifically are you experiencing symptoms?",
      locationPlaceholder: "e.g., left chest, lower back, right side of head...",
      symptoms: {
        chestPain: "Chest pain",
        shortnessOfBreath: "Shortness of breath",
        difficultyBreathing: "Difficulty breathing",
        severeBleeding: "Severe bleeding",
        lossOfConsciousness: "Loss of consciousness",
        confusion: "Confusion",
        speechDifficulty: "Speech difficulty",
        fever: "Fever",
        severeHeadache: "Severe headache",
        abdominalPain: "Abdominal pain",
        nauseaVomiting: "Nausea/Vomiting",
        dizziness: "Dizziness",
        fatigue: "Fatigue",
        backPain: "Back pain",
        jointPain: "Joint pain",
      },
    },
    vitalsRisk: {
      prefilledNote: "Some fields were pre-filled from your profile.",
      updateProfile: "Update Profile",
      vitalsTitle: "Vital Signs (optional - if you have recent measurements)",
      temperature: "Temperature (°F)",
      bloodPressure: "Blood Pressure",
      heartRate: "Heart Rate (bpm)",
      medicalHistoryTitle: "Medical History (Select all that apply)",
      conditions: {
        heartDisease: "Heart disease",
        hypertension: "High blood pressure",
        diabetes: "Diabetes",
        asthma: "Asthma",
        copd: "COPD",
        chronicKidneyDisease: "Chronic kidney disease",
        liverDisease: "Liver disease",
        cancer: "Cancer",
        immunocompromised: "Immunocompromised",
        strokeHistory: "History of stroke",
        seizureDisorder: "Seizure disorder",
        mentalHealthCondition: "Mental health condition",
      },
      medicationsTitle: "Current Medications",
      medicationsPlaceholder: "List current medications, separated by commas",
      medicationsHint: "Include prescription and over-the-counter medications, vitamins, and supplements",
      allergiesTitle: "Known Allergies",
      allergiesPlaceholder: "List allergies, separated by commas",
      allergiesHint: "Include medication allergies, food allergies, and environmental allergies",
      recentVisitsTitle: "Recent Medical Visits (past 2 weeks)",
      visits: {
        emergency: "Emergency room",
        urgentCare: "Urgent care",
        primaryCare: "Primary care doctor",
        specialist: "Specialist",
        hospitalization: "Hospital admission",
      },
      pregnancyLabel: "Currently pregnant or could be pregnant",
    },
    redFlags: {
      title: "Emergency Warning Signs",
      subtitle: "Please check any of the following symptoms you are currently experiencing. These may indicate a medical emergency that requires immediate attention.",
      emergencyAlert: "EMERGENCY: Based on your symptoms, you should seek immediate emergency care. Call 911 or go to the nearest emergency room right away.",
      criticalNote: "Critical - Requires immediate emergency care",
      noEmergencyMessage: "✓ No emergency warning signs selected. This is good - continue with your assessment.",
      disclaimer: "Important: If you are experiencing a life-threatening emergency, call 911 immediately rather than completing this assessment. This tool is not a substitute for emergency medical care.",
      symptoms: {
        severeBleeding: "Severe bleeding that won't stop",
        unconscious: "Loss of consciousness or fainting",
        difficultyBreathingSevere: "Severe difficulty breathing or can't breathe",
        chestPainSevere: "Severe chest pain or pressure",
        neurological: "Sudden confusion, slurred speech, or weakness on one side",
        severeInjury: "Severe injury from accident or trauma",
        allergicReaction: "Signs of severe allergic reaction (swelling, hives, trouble breathing)",
        suicidalThoughts: "Thoughts of hurting yourself or others",
        highFever: "Very high fever (over 103°F) with confusion",
        severeAbdominalPain: "Severe abdominal pain with vomiting",
        severeHeadache: "Sudden, severe headache unlike any before",
        visionChanges: "Sudden vision changes or loss",
      },
    },
    review: {
      title: "Review Your Assessment",
      subtitle: "Please verify all information is correct before submitting",
      sectionsCompleted: "of 4 sections completed",
      editHint: "Click on completed steps above to edit",
      emergencyAlert: "EMERGENCY ALERT: Based on your symptoms, you should seek immediate medical care. Call 911 or go to the nearest emergency room right away.",
      step1Title: "Step 1: Main Concern",
      step2Title: "Step 2: Symptom Details",
      step3Title: "Step 3: Vitals & Medical History",
      step4Title: "Step 4: Emergency Warning Signs",
      notSpecified: "Not specified",
      selectedSymptoms: "Selected Symptoms",
      noSymptomsSelected: "No symptoms selected",
      painLevel: "Pain Level",
      mild: "Mild",
      moderate: "Moderate",
      severe: "Severe",
      onset: "Onset",
      location: "Location",
      vitalSigns: "Vital Signs",
      temp: "Temp",
      bp: "BP",
      bpm: "BPM",
      medicalConditions: "Medical Conditions",
      currentMedications: "Current Medications",
      allergies: "Allergies",
      pregnant: "Currently or possibly pregnant",
      noVitalsProvided: "No vitals or medical history provided",
      noEmergencySigns: "No emergency warning signs reported",
      disclaimer: "This assessment will provide guidance on the appropriate level of care for your symptoms. It is not a diagnosis and should not replace professional medical judgment. If you feel this is a medical emergency, call 911 immediately.",
    },
  },
  es: {
    intro: {
      title: "Verificador de Síntomas con IA",
      subtitle: "Obtenga orientación médica personalizada según sus síntomas",
      howItWorks: "Cómo funciona:",
      step1: "Responda algunas preguntas sobre sus síntomas y cómo se siente",
      step2: "Solo toma 2-3 minutos completar la evaluación",
      step3: "Su información es segura y cumple con HIPAA",
      guidance: "Recibirá orientación sobre:",
      homeCare: "Cuidado en Casa",
      seeDoctor: "Ver a su Médico",
      urgentCare: "Atención Urgente",
      emergencyRoom: "Sala de Emergencias",
      disclaimer: "Esta herramienta solo proporciona orientación y no sustituye el consejo médico profesional. Si tiene una emergencia médica, llame al 911 inmediatamente.",
      startAssessment: "Iniciar Evaluación",
      skipIntro: "Omitir intro la próxima vez",
    },
    wizard: {
      stepTitles: {
        chiefComplaint: "Queja Principal",
        symptomDetails: "Detalles de Síntomas",
        vitalsRisk: "Signos Vitales y Factores de Riesgo",
        emergencySymptoms: "Síntomas de Emergencia",
        review: "Revisar",
      },
      stepDescriptions: {
        chiefComplaint: "Díganos qué le molesta más en este momento",
        symptomDetails: "Describa sus síntomas, severidad y cuándo comenzaron",
        vitalsRisk: "Comparta cualquier signo vital e historial médico relevante",
        emergencySymptoms: "Verifique cualquier señal de advertencia que necesite atención inmediata",
        review: "Revise sus respuestas y obtenga su recomendación personalizada",
      },
      navigation: {
        previous: "Anterior",
        next: "Siguiente",
        submit: "Obtener Recomendación",
        saving: "Guardando...",
        submitting: "Procesando...",
        completeAssessment: "Completar Evaluación",
        exit: "Salir",
      },
      exitDialog: {
        title: "¿Salir de la Evaluación?",
        description: "Tiene progreso sin guardar en su evaluación de salud. ¿Desea guardar su progreso antes de salir?",
        continueAssessment: "Continuar Evaluación",
        leaveWithoutSaving: "Salir sin Guardar",
        saveAndExit: "Guardar y Salir",
      },
      header: {
        title: "Evaluación de Salud",
        stepOf: "Paso {current} de {total}",
      },
    },
    chiefComplaint: {
      title: "¿Qué lo trae hoy? ¿Cuál es su principal preocupación?",
      subtitle: "Seleccione una queja común a continuación o describa su preocupación con sus propias palabras.",
      commonComplaints: "Quejas comunes:",
      orDescribe: "O describa con sus propias palabras:",
      placeholder: "Por favor describa su síntoma o preocupación principal...",
      hint: "Sea lo más específico posible. Incluya cuándo comenzó y qué lo mejora o empeora.",
      complaints: {
        chestPain: "Dolor de pecho",
        shortnessOfBreath: "Dificultad para respirar",
        headache: "Dolor de cabeza",
        abdominalPain: "Dolor abdominal",
        fever: "Fiebre",
        cough: "Tos",
        nauseaVomiting: "Náuseas/Vómitos",
        dizziness: "Mareos",
        backPain: "Dolor de espalda",
        fatigue: "Fatiga",
        woundInjury: "Herida/Lesión",
        rashSkin: "Erupción/Problema de piel",
      },
    },
    symptomDetails: {
      title: "¿Qué síntomas está experimentando? (Seleccione todos los que apliquen)",
      painSeverity: "Severidad del Dolor (0 = Sin dolor, 10 = Peor dolor posible)",
      painScale: { noPain: "Sin dolor", worstPain: "Peor dolor" },
      painLabels: { noPain: "Sin dolor", mild: "Dolor leve", moderate: "Dolor moderado", severe: "Dolor severo", worst: "Peor dolor posible" },
      symptomOnset: "¿Cuándo comenzaron sus síntomas?",
      onsetPlaceholder: "Elija cuándo comenzaron los síntomas",
      onsetOptions: {
        lessThan1Hour: "Hace menos de 1 hora",
        oneToSixHours: "Hace 1-6 horas",
        sixTo24Hours: "Hace 6-24 horas",
        oneToThreeDays: "Hace 1-3 días",
        threeToSevenDays: "Hace 3-7 días",
        moreThanWeek: "Hace más de 1 semana",
      },
      locationTitle: "¿Dónde específicamente está experimentando los síntomas?",
      locationPlaceholder: "ej., pecho izquierdo, espalda baja, lado derecho de la cabeza...",
      symptoms: {
        chestPain: "Dolor de pecho",
        shortnessOfBreath: "Dificultad para respirar",
        difficultyBreathing: "Dificultad para respirar",
        severeBleeding: "Sangrado severo",
        lossOfConsciousness: "Pérdida del conocimiento",
        confusion: "Confusión",
        speechDifficulty: "Dificultad para hablar",
        fever: "Fiebre",
        severeHeadache: "Dolor de cabeza severo",
        abdominalPain: "Dolor abdominal",
        nauseaVomiting: "Náuseas/Vómitos",
        dizziness: "Mareos",
        fatigue: "Fatiga",
        backPain: "Dolor de espalda",
        jointPain: "Dolor articular",
      },
    },
    vitalsRisk: {
      prefilledNote: "Algunos campos se completaron previamente desde su perfil.",
      updateProfile: "Actualizar Perfil",
      vitalsTitle: "Signos Vitales (opcional - si tiene mediciones recientes)",
      temperature: "Temperatura (°F)",
      bloodPressure: "Presión Arterial",
      heartRate: "Frecuencia Cardíaca (lpm)",
      medicalHistoryTitle: "Historial Médico (Seleccione todos los que apliquen)",
      conditions: {
        heartDisease: "Enfermedad cardíaca",
        hypertension: "Presión arterial alta",
        diabetes: "Diabetes",
        asthma: "Asma",
        copd: "EPOC",
        chronicKidneyDisease: "Enfermedad renal crónica",
        liverDisease: "Enfermedad hepática",
        cancer: "Cáncer",
        immunocompromised: "Inmunocomprometido",
        strokeHistory: "Historial de derrame cerebral",
        seizureDisorder: "Trastorno convulsivo",
        mentalHealthCondition: "Condición de salud mental",
      },
      medicationsTitle: "Medicamentos Actuales",
      medicationsPlaceholder: "Liste los medicamentos actuales, separados por comas",
      medicationsHint: "Incluya medicamentos con receta y de venta libre, vitaminas y suplementos",
      allergiesTitle: "Alergias Conocidas",
      allergiesPlaceholder: "Liste las alergias, separadas por comas",
      allergiesHint: "Incluya alergias a medicamentos, alimentos y ambientales",
      recentVisitsTitle: "Visitas Médicas Recientes (últimas 2 semanas)",
      visits: {
        emergency: "Sala de emergencias",
        urgentCare: "Atención urgente",
        primaryCare: "Médico de atención primaria",
        specialist: "Especialista",
        hospitalization: "Hospitalización",
      },
      pregnancyLabel: "Actualmente embarazada o podría estar embarazada",
    },
    redFlags: {
      title: "Señales de Advertencia de Emergencia",
      subtitle: "Por favor marque cualquiera de los siguientes síntomas que esté experimentando actualmente. Estos pueden indicar una emergencia médica que requiere atención inmediata.",
      emergencyAlert: "EMERGENCIA: Según sus síntomas, debe buscar atención de emergencia inmediata. Llame al 911 o vaya a la sala de emergencias más cercana de inmediato.",
      criticalNote: "Crítico - Requiere atención de emergencia inmediata",
      noEmergencyMessage: "✓ No se seleccionaron señales de advertencia de emergencia. Esto es bueno - continúe con su evaluación.",
      disclaimer: "Importante: Si está experimentando una emergencia que pone en peligro su vida, llame al 911 inmediatamente en lugar de completar esta evaluación. Esta herramienta no sustituye la atención médica de emergencia.",
      symptoms: {
        severeBleeding: "Sangrado severo que no se detiene",
        unconscious: "Pérdida del conocimiento o desmayo",
        difficultyBreathingSevere: "Dificultad severa para respirar o no puede respirar",
        chestPainSevere: "Dolor o presión severa en el pecho",
        neurological: "Confusión repentina, dificultad para hablar o debilidad en un lado",
        severeInjury: "Lesión grave por accidente o trauma",
        allergicReaction: "Signos de reacción alérgica severa (hinchazón, urticaria, dificultad para respirar)",
        suicidalThoughts: "Pensamientos de hacerse daño a sí mismo o a otros",
        highFever: "Fiebre muy alta (más de 103°F) con confusión",
        severeAbdominalPain: "Dolor abdominal severo con vómitos",
        severeHeadache: "Dolor de cabeza repentino y severo diferente a cualquier otro",
        visionChanges: "Cambios repentinos en la visión o pérdida de visión",
      },
    },
    review: {
      title: "Revise Su Evaluación",
      subtitle: "Por favor verifique que toda la información sea correcta antes de enviar",
      sectionsCompleted: "de 4 secciones completadas",
      editHint: "Haga clic en los pasos completados arriba para editar",
      emergencyAlert: "ALERTA DE EMERGENCIA: Según sus síntomas, debe buscar atención médica inmediata. Llame al 911 o vaya a la sala de emergencias más cercana de inmediato.",
      step1Title: "Paso 1: Preocupación Principal",
      step2Title: "Paso 2: Detalles de Síntomas",
      step3Title: "Paso 3: Signos Vitales e Historial Médico",
      step4Title: "Paso 4: Señales de Advertencia de Emergencia",
      notSpecified: "No especificado",
      selectedSymptoms: "Síntomas Seleccionados",
      noSymptomsSelected: "No se seleccionaron síntomas",
      painLevel: "Nivel de Dolor",
      mild: "Leve",
      moderate: "Moderado",
      severe: "Severo",
      onset: "Inicio",
      location: "Ubicación",
      vitalSigns: "Signos Vitales",
      temp: "Temp",
      bp: "PA",
      bpm: "LPM",
      medicalConditions: "Condiciones Médicas",
      currentMedications: "Medicamentos Actuales",
      allergies: "Alergias",
      pregnant: "Actualmente o posiblemente embarazada",
      noVitalsProvided: "No se proporcionaron signos vitales ni historial médico",
      noEmergencySigns: "No se reportaron señales de advertencia de emergencia",
      disclaimer: "Esta evaluación proporcionará orientación sobre el nivel apropiado de atención para sus síntomas. No es un diagnóstico y no debe reemplazar el juicio médico profesional. Si siente que esto es una emergencia médica, llame al 911 inmediatamente.",
    },
  },
  hi: {
    intro: {
      title: "AI लक्षण जांचकर्ता",
      subtitle: "अपने लक्षणों के आधार पर व्यक्तिगत स्वास्थ्य मार्गदर्शन प्राप्त करें",
      howItWorks: "यह कैसे काम करता है:",
      step1: "अपने लक्षणों और आप कैसा महसूस कर रहे हैं, इसके बारे में कुछ सवालों के जवाब दें",
      step2: "मूल्यांकन पूरा करने में केवल 2-3 मिनट लगते हैं",
      step3: "आपकी जानकारी सुरक्षित है और HIPAA के अनुपालन में है",
      guidance: "आपको इन पर मार्गदर्शन मिलेगा:",
      homeCare: "घरेलू देखभाल",
      seeDoctor: "डॉक्टर से मिलें",
      urgentCare: "तत्काल देखभाल",
      emergencyRoom: "आपातकालीन कक्ष",
      disclaimer: "यह उपकरण केवल मार्गदर्शन प्रदान करता है और पेशेवर चिकित्सा सलाह का विकल्प नहीं है। यदि आप चिकित्सा आपातकाल का अनुभव कर रहे हैं, तो तुरंत 911 पर कॉल करें।",
      startAssessment: "मूल्यांकन शुरू करें",
      skipIntro: "अगली बार परिचय छोड़ें",
    },
    wizard: {
      stepTitles: {
        chiefComplaint: "मुख्य शिकायत",
        symptomDetails: "लक्षण विवरण",
        vitalsRisk: "महत्वपूर्ण संकेत और जोखिम कारक",
        emergencySymptoms: "आपातकालीन लक्षण",
        review: "समीक्षा",
      },
      stepDescriptions: {
        chiefComplaint: "हमें बताएं कि अभी आपको सबसे ज्यादा क्या परेशान कर रहा है",
        symptomDetails: "अपने लक्षणों, गंभीरता और कब शुरू हुए इसका वर्णन करें",
        vitalsRisk: "कोई भी प्रासंगिक महत्वपूर्ण संकेत और चिकित्सा इतिहास साझा करें",
        emergencySymptoms: "किसी भी चेतावनी संकेत की जांच करें जिसे तत्काल ध्यान देने की आवश्यकता है",
        review: "अपने उत्तरों की समीक्षा करें और अपनी व्यक्तिगत सिफारिश प्राप्त करें",
      },
      navigation: {
        previous: "पिछला",
        next: "अगला",
        submit: "सिफारिश प्राप्त करें",
        saving: "सहेजा जा रहा है...",
        submitting: "प्रसंस्करण हो रहा है...",
        completeAssessment: "मूल्यांकन पूरा करें",
        exit: "बाहर निकलें",
      },
      exitDialog: {
        title: "मूल्यांकन छोड़ें?",
        description: "आपके स्वास्थ्य मूल्यांकन में असहेजी प्रगति है। क्या आप छोड़ने से पहले अपनी प्रगति सहेजना चाहेंगे?",
        continueAssessment: "मूल्यांकन जारी रखें",
        leaveWithoutSaving: "बिना सहेजे छोड़ें",
        saveAndExit: "सहेजें और बाहर निकलें",
      },
      header: {
        title: "स्वास्थ्य मूल्यांकन",
        stepOf: "चरण {current} में से {total}",
      },
    },
    chiefComplaint: {
      title: "आज आप यहाँ क्यों आए हैं? आपकी मुख्य चिंता क्या है?",
      subtitle: "नीचे एक सामान्य शिकायत चुनें या अपने शब्दों में अपनी चिंता का वर्णन करें।",
      commonComplaints: "सामान्य शिकायतें:",
      orDescribe: "या अपने शब्दों में वर्णन करें:",
      placeholder: "कृपया अपने मुख्य लक्षण या चिंता का वर्णन करें...",
      hint: "जितना संभव हो उतना विशिष्ट रहें। बताएं कि यह कब शुरू हुआ और क्या इसे बेहतर या बदतर बनाता है।",
      complaints: {
        chestPain: "छाती में दर्द",
        shortnessOfBreath: "सांस की तकलीफ",
        headache: "सिरदर्द",
        abdominalPain: "पेट दर्द",
        fever: "बुखार",
        cough: "खांसी",
        nauseaVomiting: "मतली/उल्टी",
        dizziness: "चक्कर आना",
        backPain: "पीठ दर्द",
        fatigue: "थकान",
        woundInjury: "घाव/चोट",
        rashSkin: "दाने/त्वचा की समस्या",
      },
    },
    symptomDetails: {
      title: "आप किन लक्षणों का अनुभव कर रहे हैं? (सभी लागू होने वाले चुनें)",
      painSeverity: "दर्द की गंभीरता (0 = कोई दर्द नहीं, 10 = सबसे खराब संभव दर्द)",
      painScale: { noPain: "कोई दर्द नहीं", worstPain: "सबसे खराब दर्द" },
      painLabels: { noPain: "कोई दर्द नहीं", mild: "हल्का दर्द", moderate: "मध्यम दर्द", severe: "गंभीर दर्द", worst: "सबसे खराब संभव दर्द" },
      symptomOnset: "आपके लक्षण कब शुरू हुए?",
      onsetPlaceholder: "चुनें कि लक्षण कब शुरू हुए",
      onsetOptions: {
        lessThan1Hour: "1 घंटे से कम पहले",
        oneToSixHours: "1-6 घंटे पहले",
        sixTo24Hours: "6-24 घंटे पहले",
        oneToThreeDays: "1-3 दिन पहले",
        threeToSevenDays: "3-7 दिन पहले",
        moreThanWeek: "1 सप्ताह से अधिक पहले",
      },
      locationTitle: "आप विशेष रूप से कहाँ लक्षणों का अनुभव कर रहे हैं?",
      locationPlaceholder: "उदा., बाईं छाती, पीठ के निचले हिस्से में, सिर के दाईं ओर...",
      symptoms: {
        chestPain: "छाती में दर्द",
        shortnessOfBreath: "सांस की तकलीफ",
        difficultyBreathing: "सांस लेने में कठिनाई",
        severeBleeding: "गंभीर रक्तस्राव",
        lossOfConsciousness: "बेहोशी",
        confusion: "भ्रम",
        speechDifficulty: "बोलने में कठिनाई",
        fever: "बुखार",
        severeHeadache: "गंभीर सिरदर्द",
        abdominalPain: "पेट दर्द",
        nauseaVomiting: "मतली/उल्टी",
        dizziness: "चक्कर आना",
        fatigue: "थकान",
        backPain: "पीठ दर्द",
        jointPain: "जोड़ों का दर्द",
      },
    },
    vitalsRisk: {
      prefilledNote: "कुछ फ़ील्ड आपकी प्रोफ़ाइल से पहले से भरे गए थे।",
      updateProfile: "प्रोफ़ाइल अपडेट करें",
      vitalsTitle: "महत्वपूर्ण संकेत (वैकल्पिक - यदि आपके पास हाल के माप हैं)",
      temperature: "तापमान (°F)",
      bloodPressure: "रक्तचाप",
      heartRate: "हृदय गति (bpm)",
      medicalHistoryTitle: "चिकित्सा इतिहास (सभी लागू होने वाले चुनें)",
      conditions: {
        heartDisease: "हृदय रोग",
        hypertension: "उच्च रक्तचाप",
        diabetes: "मधुमेह",
        asthma: "अस्थमा",
        copd: "सीओपीडी",
        chronicKidneyDisease: "क्रोनिक किडनी रोग",
        liverDisease: "यकृत रोग",
        cancer: "कैंसर",
        immunocompromised: "प्रतिरक्षा में कमी",
        strokeHistory: "स्ट्रोक का इतिहास",
        seizureDisorder: "दौरे का विकार",
        mentalHealthCondition: "मानसिक स्वास्थ्य स्थिति",
      },
      medicationsTitle: "वर्तमान दवाइयां",
      medicationsPlaceholder: "वर्तमान दवाइयां सूचीबद्ध करें, अल्पविराम से अलग करें",
      medicationsHint: "प्रिस्क्रिप्शन और ओवर-द-काउंटर दवाइयां, विटामिन और सप्लीमेंट शामिल करें",
      allergiesTitle: "ज्ञात एलर्जी",
      allergiesPlaceholder: "एलर्जी सूचीबद्ध करें, अल्पविराम से अलग करें",
      allergiesHint: "दवा एलर्जी, खाद्य एलर्जी और पर्यावरणीय एलर्जी शामिल करें",
      recentVisitsTitle: "हाल की चिकित्सा यात्राएं (पिछले 2 सप्ताह)",
      visits: {
        emergency: "आपातकालीन कक्ष",
        urgentCare: "तत्काल देखभाल",
        primaryCare: "प्राथमिक देखभाल डॉक्टर",
        specialist: "विशेषज्ञ",
        hospitalization: "अस्पताल में भर्ती",
      },
      pregnancyLabel: "वर्तमान में गर्भवती हैं या गर्भवती हो सकती हैं",
    },
    redFlags: {
      title: "आपातकालीन चेतावनी संकेत",
      subtitle: "कृपया निम्नलिखित में से कोई भी लक्षण जो आप वर्तमान में अनुभव कर रहे हैं उसे चिह्नित करें। ये चिकित्सा आपातकाल का संकेत दे सकते हैं जिसे तत्काल ध्यान देने की आवश्यकता है।",
      emergencyAlert: "आपातकाल: आपके लक्षणों के आधार पर, आपको तत्काल आपातकालीन देखभाल लेनी चाहिए। 911 पर कॉल करें या तुरंत निकटतम आपातकालीन कक्ष में जाएं।",
      criticalNote: "गंभीर - तत्काल आपातकालीन देखभाल की आवश्यकता है",
      noEmergencyMessage: "✓ कोई आपातकालीन चेतावनी संकेत नहीं चुना गया। यह अच्छा है - अपने मूल्यांकन के साथ जारी रखें।",
      disclaimer: "महत्वपूर्ण: यदि आप जीवन-धमकी वाली आपातकाल का अनुभव कर रहे हैं, तो इस मूल्यांकन को पूरा करने के बजाय तुरंत 911 पर कॉल करें। यह उपकरण आपातकालीन चिकित्सा देखभाल का विकल्प नहीं है।",
      symptoms: {
        severeBleeding: "गंभीर रक्तस्राव जो रुक नहीं रहा",
        unconscious: "बेहोशी या मूर्छा",
        difficultyBreathingSevere: "सांस लेने में गंभीर कठिनाई या सांस नहीं ले पा रहे",
        chestPainSevere: "गंभीर छाती में दर्द या दबाव",
        neurological: "अचानक भ्रम, अस्पष्ट भाषण, या एक तरफ कमजोरी",
        severeInjury: "दुर्घटना या आघात से गंभीर चोट",
        allergicReaction: "गंभीर एलर्जी प्रतिक्रिया के संकेत (सूजन, पित्ती, सांस लेने में कठिनाई)",
        suicidalThoughts: "खुद को या दूसरों को नुकसान पहुंचाने के विचार",
        highFever: "बहुत तेज बुखार (103°F से अधिक) भ्रम के साथ",
        severeAbdominalPain: "उल्टी के साथ गंभीर पेट दर्द",
        severeHeadache: "अचानक, गंभीर सिरदर्द जो पहले कभी नहीं हुआ",
        visionChanges: "अचानक दृष्टि में परिवर्तन या हानि",
      },
    },
    review: {
      title: "अपने मूल्यांकन की समीक्षा करें",
      subtitle: "कृपया जमा करने से पहले सत्यापित करें कि सभी जानकारी सही है",
      sectionsCompleted: "4 में से अनुभाग पूर्ण",
      editHint: "संपादित करने के लिए ऊपर पूर्ण चरणों पर क्लिक करें",
      emergencyAlert: "आपातकालीन अलर्ट: आपके लक्षणों के आधार पर, आपको तत्काल चिकित्सा देखभाल लेनी चाहिए। 911 पर कॉल करें या तुरंत निकटतम आपातकालीन कक्ष में जाएं।",
      step1Title: "चरण 1: मुख्य चिंता",
      step2Title: "चरण 2: लक्षण विवरण",
      step3Title: "चरण 3: महत्वपूर्ण संकेत और चिकित्सा इतिहास",
      step4Title: "चरण 4: आपातकालीन चेतावनी संकेत",
      notSpecified: "निर्दिष्ट नहीं",
      selectedSymptoms: "चयनित लक्षण",
      noSymptomsSelected: "कोई लक्षण नहीं चुने गए",
      painLevel: "दर्द का स्तर",
      mild: "हल्का",
      moderate: "मध्यम",
      severe: "गंभीर",
      onset: "शुरुआत",
      location: "स्थान",
      vitalSigns: "महत्वपूर्ण संकेत",
      temp: "तापमान",
      bp: "बीपी",
      bpm: "BPM",
      medicalConditions: "चिकित्सा स्थितियां",
      currentMedications: "वर्तमान दवाइयां",
      allergies: "एलर्जी",
      pregnant: "वर्तमान में या संभवतः गर्भवती",
      noVitalsProvided: "कोई महत्वपूर्ण संकेत या चिकित्सा इतिहास प्रदान नहीं किया गया",
      noEmergencySigns: "कोई आपातकालीन चेतावनी संकेत रिपोर्ट नहीं किए गए",
      disclaimer: "यह मूल्यांकन आपके लक्षणों के लिए उचित स्तर की देखभाल पर मार्गदर्शन प्रदान करेगा। यह निदान नहीं है और पेशेवर चिकित्सा निर्णय को प्रतिस्थापित नहीं करना चाहिए। यदि आपको लगता है कि यह चिकित्सा आपातकाल है, तो तुरंत 911 पर कॉल करें।",
    },
  },
  pa: {
    intro: {
      title: "AI ਲੱਛਣ ਜਾਂਚਕਰਤਾ",
      subtitle: "ਆਪਣੇ ਲੱਛਣਾਂ ਦੇ ਆਧਾਰ 'ਤੇ ਵਿਅਕਤੀਗਤ ਸਿਹਤ ਮਾਰਗਦਰਸ਼ਨ ਪ੍ਰਾਪਤ ਕਰੋ",
      howItWorks: "ਇਹ ਕਿਵੇਂ ਕੰਮ ਕਰਦਾ ਹੈ:",
      step1: "ਆਪਣੇ ਲੱਛਣਾਂ ਅਤੇ ਤੁਸੀਂ ਕਿਵੇਂ ਮਹਿਸੂਸ ਕਰ ਰਹੇ ਹੋ ਬਾਰੇ ਕੁਝ ਸਵਾਲਾਂ ਦੇ ਜਵਾਬ ਦਿਓ",
      step2: "ਮੁਲਾਂਕਣ ਪੂਰਾ ਕਰਨ ਲਈ ਸਿਰਫ਼ 2-3 ਮਿੰਟ ਲੱਗਦੇ ਹਨ",
      step3: "ਤੁਹਾਡੀ ਜਾਣਕਾਰੀ ਸੁਰੱਖਿਅਤ ਹੈ ਅਤੇ HIPAA ਦੇ ਅਨੁਕੂਲ ਹੈ",
      guidance: "ਤੁਹਾਨੂੰ ਇਨ੍ਹਾਂ ਬਾਰੇ ਮਾਰਗਦਰਸ਼ਨ ਮਿਲੇਗਾ:",
      homeCare: "ਘਰੇਲੂ ਦੇਖਭਾਲ",
      seeDoctor: "ਡਾਕਟਰ ਨੂੰ ਮਿਲੋ",
      urgentCare: "ਫੌਰੀ ਦੇਖਭਾਲ",
      emergencyRoom: "ਐਮਰਜੈਂਸੀ ਕਮਰਾ",
      disclaimer: "ਇਹ ਸੰਦ ਸਿਰਫ਼ ਮਾਰਗਦਰਸ਼ਨ ਪ੍ਰਦਾਨ ਕਰਦਾ ਹੈ ਅਤੇ ਪੇਸ਼ੇਵਰ ਡਾਕਟਰੀ ਸਲਾਹ ਦਾ ਬਦਲ ਨਹੀਂ ਹੈ। ਜੇ ਤੁਸੀਂ ਡਾਕਟਰੀ ਐਮਰਜੈਂਸੀ ਦਾ ਅਨੁਭਵ ਕਰ ਰਹੇ ਹੋ, ਤਾਂ ਤੁਰੰਤ 911 'ਤੇ ਕਾਲ ਕਰੋ।",
      startAssessment: "ਮੁਲਾਂਕਣ ਸ਼ੁਰੂ ਕਰੋ",
      skipIntro: "ਅਗਲੀ ਵਾਰ ਜਾਣ-ਪਛਾਣ ਛੱਡੋ",
    },
    wizard: {
      stepTitles: {
        chiefComplaint: "ਮੁੱਖ ਸ਼ਿਕਾਇਤ",
        symptomDetails: "ਲੱਛਣ ਵੇਰਵੇ",
        vitalsRisk: "ਮਹੱਤਵਪੂਰਨ ਸੰਕੇਤ ਅਤੇ ਜੋਖਮ ਕਾਰਕ",
        emergencySymptoms: "ਐਮਰਜੈਂਸੀ ਲੱਛਣ",
        review: "ਸਮੀਖਿਆ",
      },
      stepDescriptions: {
        chiefComplaint: "ਸਾਨੂੰ ਦੱਸੋ ਕਿ ਹੁਣੇ ਤੁਹਾਨੂੰ ਸਭ ਤੋਂ ਵੱਧ ਕੀ ਪਰੇਸ਼ਾਨ ਕਰ ਰਿਹਾ ਹੈ",
        symptomDetails: "ਆਪਣੇ ਲੱਛਣਾਂ, ਗੰਭੀਰਤਾ, ਅਤੇ ਕਦੋਂ ਸ਼ੁਰੂ ਹੋਏ ਦਾ ਵਰਣਨ ਕਰੋ",
        vitalsRisk: "ਕੋਈ ਵੀ ਸੰਬੰਧਿਤ ਮਹੱਤਵਪੂਰਨ ਸੰਕੇਤ ਅਤੇ ਡਾਕਟਰੀ ਇਤਿਹਾਸ ਸਾਂਝਾ ਕਰੋ",
        emergencySymptoms: "ਕਿਸੇ ਵੀ ਚੇਤਾਵਨੀ ਸੰਕੇਤ ਦੀ ਜਾਂਚ ਕਰੋ ਜਿਸ ਨੂੰ ਤੁਰੰਤ ਧਿਆਨ ਦੀ ਲੋੜ ਹੈ",
        review: "ਆਪਣੇ ਜਵਾਬਾਂ ਦੀ ਸਮੀਖਿਆ ਕਰੋ ਅਤੇ ਆਪਣੀ ਵਿਅਕਤੀਗਤ ਸਿਫ਼ਾਰਸ਼ ਪ੍ਰਾਪਤ ਕਰੋ",
      },
      navigation: {
        previous: "ਪਿਛਲਾ",
        next: "ਅਗਲਾ",
        submit: "ਸਿਫ਼ਾਰਸ਼ ਪ੍ਰਾਪਤ ਕਰੋ",
        saving: "ਸੇਵ ਹੋ ਰਿਹਾ ਹੈ...",
        submitting: "ਪ੍ਰੋਸੈਸ ਹੋ ਰਿਹਾ ਹੈ...",
        completeAssessment: "ਮੁਲਾਂਕਣ ਪੂਰਾ ਕਰੋ",
        exit: "ਬਾਹਰ ਜਾਓ",
      },
      exitDialog: {
        title: "ਮੁਲਾਂਕਣ ਛੱਡੋ?",
        description: "ਤੁਹਾਡੇ ਸਿਹਤ ਮੁਲਾਂਕਣ ਵਿੱਚ ਅਣਸੇਵਡ ਪ੍ਰਗਤੀ ਹੈ। ਕੀ ਤੁਸੀਂ ਛੱਡਣ ਤੋਂ ਪਹਿਲਾਂ ਆਪਣੀ ਪ੍ਰਗਤੀ ਸੇਵ ਕਰਨਾ ਚਾਹੋਗੇ?",
        continueAssessment: "ਮੁਲਾਂਕਣ ਜਾਰੀ ਰੱਖੋ",
        leaveWithoutSaving: "ਬਿਨਾਂ ਸੇਵ ਕੀਤੇ ਛੱਡੋ",
        saveAndExit: "ਸੇਵ ਕਰੋ ਅਤੇ ਬਾਹਰ ਜਾਓ",
      },
      header: {
        title: "ਸਿਹਤ ਮੁਲਾਂਕਣ",
        stepOf: "ਕਦਮ {current} ਵਿੱਚੋਂ {total}",
      },
    },
    chiefComplaint: {
      title: "ਅੱਜ ਤੁਸੀਂ ਇੱਥੇ ਕਿਉਂ ਆਏ ਹੋ? ਤੁਹਾਡੀ ਮੁੱਖ ਚਿੰਤਾ ਕੀ ਹੈ?",
      subtitle: "ਹੇਠਾਂ ਇੱਕ ਆਮ ਸ਼ਿਕਾਇਤ ਚੁਣੋ ਜਾਂ ਆਪਣੇ ਸ਼ਬਦਾਂ ਵਿੱਚ ਆਪਣੀ ਚਿੰਤਾ ਦਾ ਵਰਣਨ ਕਰੋ।",
      commonComplaints: "ਆਮ ਸ਼ਿਕਾਇਤਾਂ:",
      orDescribe: "ਜਾਂ ਆਪਣੇ ਸ਼ਬਦਾਂ ਵਿੱਚ ਵਰਣਨ ਕਰੋ:",
      placeholder: "ਕਿਰਪਾ ਕਰਕੇ ਆਪਣੇ ਮੁੱਖ ਲੱਛਣ ਜਾਂ ਚਿੰਤਾ ਦਾ ਵਰਣਨ ਕਰੋ...",
      hint: "ਜਿੰਨਾ ਹੋ ਸਕੇ ਵਿਸਤਾਰ ਨਾਲ ਦੱਸੋ। ਦੱਸੋ ਕਿ ਇਹ ਕਦੋਂ ਸ਼ੁਰੂ ਹੋਇਆ ਅਤੇ ਕੀ ਇਸ ਨੂੰ ਬਿਹਤਰ ਜਾਂ ਮਾੜਾ ਬਣਾਉਂਦਾ ਹੈ।",
      complaints: {
        chestPain: "ਛਾਤੀ ਵਿੱਚ ਦਰਦ",
        shortnessOfBreath: "ਸਾਹ ਦੀ ਤਕਲੀਫ਼",
        headache: "ਸਿਰ ਦਰਦ",
        abdominalPain: "ਪੇਟ ਦਰਦ",
        fever: "ਬੁਖ਼ਾਰ",
        cough: "ਖੰਘ",
        nauseaVomiting: "ਮਤਲੀ/ਉਲਟੀ",
        dizziness: "ਚੱਕਰ ਆਉਣਾ",
        backPain: "ਪਿੱਠ ਦਰਦ",
        fatigue: "ਥਕਾਵਟ",
        woundInjury: "ਜ਼ਖ਼ਮ/ਸੱਟ",
        rashSkin: "ਧੱਫੜ/ਚਮੜੀ ਦੀ ਸਮੱਸਿਆ",
      },
    },
    symptomDetails: {
      title: "ਤੁਸੀਂ ਕਿਹੜੇ ਲੱਛਣਾਂ ਦਾ ਅਨੁਭਵ ਕਰ ਰਹੇ ਹੋ? (ਸਾਰੇ ਲਾਗੂ ਹੋਣ ਵਾਲੇ ਚੁਣੋ)",
      painSeverity: "ਦਰਦ ਦੀ ਗੰਭੀਰਤਾ (0 = ਕੋਈ ਦਰਦ ਨਹੀਂ, 10 = ਸਭ ਤੋਂ ਮਾੜਾ ਸੰਭਵ ਦਰਦ)",
      painScale: { noPain: "ਕੋਈ ਦਰਦ ਨਹੀਂ", worstPain: "ਸਭ ਤੋਂ ਮਾੜਾ ਦਰਦ" },
      painLabels: { noPain: "ਕੋਈ ਦਰਦ ਨਹੀਂ", mild: "ਹਲਕਾ ਦਰਦ", moderate: "ਮੱਧਮ ਦਰਦ", severe: "ਗੰਭੀਰ ਦਰਦ", worst: "ਸਭ ਤੋਂ ਮਾੜਾ ਸੰਭਵ ਦਰਦ" },
      symptomOnset: "ਤੁਹਾਡੇ ਲੱਛਣ ਕਦੋਂ ਸ਼ੁਰੂ ਹੋਏ?",
      onsetPlaceholder: "ਚੁਣੋ ਕਿ ਲੱਛਣ ਕਦੋਂ ਸ਼ੁਰੂ ਹੋਏ",
      onsetOptions: {
        lessThan1Hour: "1 ਘੰਟੇ ਤੋਂ ਘੱਟ ਪਹਿਲਾਂ",
        oneToSixHours: "1-6 ਘੰਟੇ ਪਹਿਲਾਂ",
        sixTo24Hours: "6-24 ਘੰਟੇ ਪਹਿਲਾਂ",
        oneToThreeDays: "1-3 ਦਿਨ ਪਹਿਲਾਂ",
        threeToSevenDays: "3-7 ਦਿਨ ਪਹਿਲਾਂ",
        moreThanWeek: "1 ਹਫ਼ਤੇ ਤੋਂ ਵੱਧ ਪਹਿਲਾਂ",
      },
      locationTitle: "ਤੁਸੀਂ ਖਾਸ ਤੌਰ 'ਤੇ ਕਿੱਥੇ ਲੱਛਣਾਂ ਦਾ ਅਨੁਭਵ ਕਰ ਰਹੇ ਹੋ?",
      locationPlaceholder: "ਉਦਾ., ਖੱਬੀ ਛਾਤੀ, ਹੇਠਲੀ ਪਿੱਠ, ਸਿਰ ਦੇ ਸੱਜੇ ਪਾਸੇ...",
      symptoms: {
        chestPain: "ਛਾਤੀ ਵਿੱਚ ਦਰਦ",
        shortnessOfBreath: "ਸਾਹ ਦੀ ਤਕਲੀਫ਼",
        difficultyBreathing: "ਸਾਹ ਲੈਣ ਵਿੱਚ ਮੁਸ਼ਕਲ",
        severeBleeding: "ਗੰਭੀਰ ਖੂਨ ਵਹਿਣਾ",
        lossOfConsciousness: "ਬੇਹੋਸ਼ੀ",
        confusion: "ਉਲਝਣ",
        speechDifficulty: "ਬੋਲਣ ਵਿੱਚ ਮੁਸ਼ਕਲ",
        fever: "ਬੁਖ਼ਾਰ",
        severeHeadache: "ਗੰਭੀਰ ਸਿਰ ਦਰਦ",
        abdominalPain: "ਪੇਟ ਦਰਦ",
        nauseaVomiting: "ਮਤਲੀ/ਉਲਟੀ",
        dizziness: "ਚੱਕਰ ਆਉਣਾ",
        fatigue: "ਥਕਾਵਟ",
        backPain: "ਪਿੱਠ ਦਰਦ",
        jointPain: "ਜੋੜਾਂ ਦਾ ਦਰਦ",
      },
    },
    vitalsRisk: {
      prefilledNote: "ਕੁਝ ਫ਼ੀਲਡ ਤੁਹਾਡੀ ਪ੍ਰੋਫ਼ਾਈਲ ਤੋਂ ਪਹਿਲਾਂ ਹੀ ਭਰੇ ਗਏ ਸਨ।",
      updateProfile: "ਪ੍ਰੋਫ਼ਾਈਲ ਅੱਪਡੇਟ ਕਰੋ",
      vitalsTitle: "ਮਹੱਤਵਪੂਰਨ ਸੰਕੇਤ (ਵਿਕਲਪਿਕ - ਜੇ ਤੁਹਾਡੇ ਕੋਲ ਹਾਲੀਆ ਮਾਪ ਹਨ)",
      temperature: "ਤਾਪਮਾਨ (°F)",
      bloodPressure: "ਬਲੱਡ ਪ੍ਰੈਸ਼ਰ",
      heartRate: "ਦਿਲ ਦੀ ਧੜਕਣ (bpm)",
      medicalHistoryTitle: "ਡਾਕਟਰੀ ਇਤਿਹਾਸ (ਸਾਰੇ ਲਾਗੂ ਹੋਣ ਵਾਲੇ ਚੁਣੋ)",
      conditions: {
        heartDisease: "ਦਿਲ ਦੀ ਬਿਮਾਰੀ",
        hypertension: "ਉੱਚ ਬਲੱਡ ਪ੍ਰੈਸ਼ਰ",
        diabetes: "ਸ਼ੂਗਰ",
        asthma: "ਦਮਾ",
        copd: "COPD",
        chronicKidneyDisease: "ਗੁਰਦੇ ਦੀ ਪੁਰਾਣੀ ਬਿਮਾਰੀ",
        liverDisease: "ਜਿਗਰ ਦੀ ਬਿਮਾਰੀ",
        cancer: "ਕੈਂਸਰ",
        immunocompromised: "ਇਮਿਊਨ ਸਿਸਟਮ ਕਮਜ਼ੋਰ",
        strokeHistory: "ਸਟ੍ਰੋਕ ਦਾ ਇਤਿਹਾਸ",
        seizureDisorder: "ਦੌਰੇ ਦਾ ਵਿਕਾਰ",
        mentalHealthCondition: "ਮਾਨਸਿਕ ਸਿਹਤ ਸਥਿਤੀ",
      },
      medicationsTitle: "ਮੌਜੂਦਾ ਦਵਾਈਆਂ",
      medicationsPlaceholder: "ਮੌਜੂਦਾ ਦਵਾਈਆਂ ਸੂਚੀਬੱਧ ਕਰੋ, ਕਾਮੇ ਨਾਲ ਵੱਖ ਕਰੋ",
      medicationsHint: "ਨੁਸਖ਼ੇ ਅਤੇ ਓਵਰ-ਦ-ਕਾਊਂਟਰ ਦਵਾਈਆਂ, ਵਿਟਾਮਿਨ ਅਤੇ ਸਪਲੀਮੈਂਟ ਸ਼ਾਮਲ ਕਰੋ",
      allergiesTitle: "ਜਾਣੀਆਂ-ਪਛਾਣੀਆਂ ਐਲਰਜੀਆਂ",
      allergiesPlaceholder: "ਐਲਰਜੀਆਂ ਸੂਚੀਬੱਧ ਕਰੋ, ਕਾਮੇ ਨਾਲ ਵੱਖ ਕਰੋ",
      allergiesHint: "ਦਵਾਈ ਐਲਰਜੀ, ਭੋਜਨ ਐਲਰਜੀ ਅਤੇ ਵਾਤਾਵਰਣ ਐਲਰਜੀ ਸ਼ਾਮਲ ਕਰੋ",
      recentVisitsTitle: "ਹਾਲੀਆ ਡਾਕਟਰੀ ਮੁਲਾਕਾਤਾਂ (ਪਿਛਲੇ 2 ਹਫ਼ਤੇ)",
      visits: {
        emergency: "ਐਮਰਜੈਂਸੀ ਕਮਰਾ",
        urgentCare: "ਫੌਰੀ ਦੇਖਭਾਲ",
        primaryCare: "ਪ੍ਰਾਇਮਰੀ ਕੇਅਰ ਡਾਕਟਰ",
        specialist: "ਮਾਹਰ",
        hospitalization: "ਹਸਪਤਾਲ ਵਿੱਚ ਦਾਖਲਾ",
      },
      pregnancyLabel: "ਮੌਜੂਦਾ ਸਮੇਂ ਗਰਭਵਤੀ ਹੋ ਜਾਂ ਹੋ ਸਕਦੀ ਹੋ",
    },
    redFlags: {
      title: "ਐਮਰਜੈਂਸੀ ਚੇਤਾਵਨੀ ਸੰਕੇਤ",
      subtitle: "ਕਿਰਪਾ ਕਰਕੇ ਹੇਠਾਂ ਦਿੱਤੇ ਕਿਸੇ ਵੀ ਲੱਛਣ ਨੂੰ ਚਿੰਨ੍ਹਿਤ ਕਰੋ ਜੋ ਤੁਸੀਂ ਮੌਜੂਦਾ ਸਮੇਂ ਅਨੁਭਵ ਕਰ ਰਹੇ ਹੋ। ਇਹ ਡਾਕਟਰੀ ਐਮਰਜੈਂਸੀ ਦਾ ਸੰਕੇਤ ਹੋ ਸਕਦੇ ਹਨ ਜਿਸ ਨੂੰ ਤੁਰੰਤ ਧਿਆਨ ਦੀ ਲੋੜ ਹੈ।",
      emergencyAlert: "ਐਮਰਜੈਂਸੀ: ਤੁਹਾਡੇ ਲੱਛਣਾਂ ਦੇ ਆਧਾਰ 'ਤੇ, ਤੁਹਾਨੂੰ ਤੁਰੰਤ ਐਮਰਜੈਂਸੀ ਦੇਖਭਾਲ ਲੈਣੀ ਚਾਹੀਦੀ ਹੈ। 911 'ਤੇ ਕਾਲ ਕਰੋ ਜਾਂ ਤੁਰੰਤ ਨਜ਼ਦੀਕੀ ਐਮਰਜੈਂਸੀ ਕਮਰੇ ਵਿੱਚ ਜਾਓ।",
      criticalNote: "ਗੰਭੀਰ - ਤੁਰੰਤ ਐਮਰਜੈਂਸੀ ਦੇਖਭਾਲ ਦੀ ਲੋੜ ਹੈ",
      noEmergencyMessage: "✓ ਕੋਈ ਐਮਰਜੈਂਸੀ ਚੇਤਾਵਨੀ ਸੰਕੇਤ ਨਹੀਂ ਚੁਣੇ ਗਏ। ਇਹ ਚੰਗਾ ਹੈ - ਆਪਣੇ ਮੁਲਾਂਕਣ ਨਾਲ ਜਾਰੀ ਰੱਖੋ।",
      disclaimer: "ਮਹੱਤਵਪੂਰਨ: ਜੇ ਤੁਸੀਂ ਜਾਨਲੇਵਾ ਐਮਰਜੈਂਸੀ ਦਾ ਅਨੁਭਵ ਕਰ ਰਹੇ ਹੋ, ਤਾਂ ਇਸ ਮੁਲਾਂਕਣ ਨੂੰ ਪੂਰਾ ਕਰਨ ਦੀ ਬਜਾਏ ਤੁਰੰਤ 911 'ਤੇ ਕਾਲ ਕਰੋ। ਇਹ ਸੰਦ ਐਮਰਜੈਂਸੀ ਡਾਕਟਰੀ ਦੇਖਭਾਲ ਦਾ ਬਦਲ ਨਹੀਂ ਹੈ।",
      symptoms: {
        severeBleeding: "ਗੰਭੀਰ ਖੂਨ ਵਹਿਣਾ ਜੋ ਰੁਕ ਨਹੀਂ ਰਿਹਾ",
        unconscious: "ਬੇਹੋਸ਼ੀ ਜਾਂ ਬੇਹੋਸ਼ ਹੋਣਾ",
        difficultyBreathingSevere: "ਸਾਹ ਲੈਣ ਵਿੱਚ ਗੰਭੀਰ ਮੁਸ਼ਕਲ ਜਾਂ ਸਾਹ ਨਹੀਂ ਲੈ ਸਕਦੇ",
        chestPainSevere: "ਗੰਭੀਰ ਛਾਤੀ ਵਿੱਚ ਦਰਦ ਜਾਂ ਦਬਾਅ",
        neurological: "ਅਚਾਨਕ ਉਲਝਣ, ਗੱਲ ਕਰਨ ਵਿੱਚ ਮੁਸ਼ਕਲ, ਜਾਂ ਇੱਕ ਪਾਸੇ ਕਮਜ਼ੋਰੀ",
        severeInjury: "ਦੁਰਘਟਨਾ ਜਾਂ ਸੱਟ ਤੋਂ ਗੰਭੀਰ ਸੱਟ",
        allergicReaction: "ਗੰਭੀਰ ਐਲਰਜੀ ਪ੍ਰਤੀਕਿਰਿਆ ਦੇ ਸੰਕੇਤ (ਸੋਜ, ਛਪਾਕੀ, ਸਾਹ ਲੈਣ ਵਿੱਚ ਮੁਸ਼ਕਲ)",
        suicidalThoughts: "ਆਪਣੇ ਆਪ ਨੂੰ ਜਾਂ ਦੂਜਿਆਂ ਨੂੰ ਨੁਕਸਾਨ ਪਹੁੰਚਾਉਣ ਦੇ ਵਿਚਾਰ",
        highFever: "ਬਹੁਤ ਤੇਜ਼ ਬੁਖ਼ਾਰ (103°F ਤੋਂ ਵੱਧ) ਉਲਝਣ ਦੇ ਨਾਲ",
        severeAbdominalPain: "ਉਲਟੀ ਦੇ ਨਾਲ ਗੰਭੀਰ ਪੇਟ ਦਰਦ",
        severeHeadache: "ਅਚਾਨਕ, ਗੰਭੀਰ ਸਿਰ ਦਰਦ ਜੋ ਪਹਿਲਾਂ ਕਦੇ ਨਹੀਂ ਹੋਇਆ",
        visionChanges: "ਅਚਾਨਕ ਨਜ਼ਰ ਵਿੱਚ ਬਦਲਾਅ ਜਾਂ ਨੁਕਸਾਨ",
      },
    },
    review: {
      title: "ਆਪਣੇ ਮੁਲਾਂਕਣ ਦੀ ਸਮੀਖਿਆ ਕਰੋ",
      subtitle: "ਕਿਰਪਾ ਕਰਕੇ ਜਮ੍ਹਾਂ ਕਰਨ ਤੋਂ ਪਹਿਲਾਂ ਪੁਸ਼ਟੀ ਕਰੋ ਕਿ ਸਾਰੀ ਜਾਣਕਾਰੀ ਸਹੀ ਹੈ",
      sectionsCompleted: "4 ਵਿੱਚੋਂ ਭਾਗ ਪੂਰੇ",
      editHint: "ਸੰਪਾਦਿਤ ਕਰਨ ਲਈ ਉੱਪਰ ਪੂਰੇ ਕੀਤੇ ਕਦਮਾਂ 'ਤੇ ਕਲਿੱਕ ਕਰੋ",
      emergencyAlert: "ਐਮਰਜੈਂਸੀ ਅਲਰਟ: ਤੁਹਾਡੇ ਲੱਛਣਾਂ ਦੇ ਆਧਾਰ 'ਤੇ, ਤੁਹਾਨੂੰ ਤੁਰੰਤ ਡਾਕਟਰੀ ਦੇਖਭਾਲ ਲੈਣੀ ਚਾਹੀਦੀ ਹੈ। 911 'ਤੇ ਕਾਲ ਕਰੋ ਜਾਂ ਤੁਰੰਤ ਨਜ਼ਦੀਕੀ ਐਮਰਜੈਂਸੀ ਕਮਰੇ ਵਿੱਚ ਜਾਓ।",
      step1Title: "ਕਦਮ 1: ਮੁੱਖ ਚਿੰਤਾ",
      step2Title: "ਕਦਮ 2: ਲੱਛਣ ਵੇਰਵੇ",
      step3Title: "ਕਦਮ 3: ਮਹੱਤਵਪੂਰਨ ਸੰਕੇਤ ਅਤੇ ਡਾਕਟਰੀ ਇਤਿਹਾਸ",
      step4Title: "ਕਦਮ 4: ਐਮਰਜੈਂਸੀ ਚੇਤਾਵਨੀ ਸੰਕੇਤ",
      notSpecified: "ਨਿਰਧਾਰਿਤ ਨਹੀਂ",
      selectedSymptoms: "ਚੁਣੇ ਗਏ ਲੱਛਣ",
      noSymptomsSelected: "ਕੋਈ ਲੱਛਣ ਨਹੀਂ ਚੁਣੇ ਗਏ",
      painLevel: "ਦਰਦ ਦਾ ਪੱਧਰ",
      mild: "ਹਲਕਾ",
      moderate: "ਮੱਧਮ",
      severe: "ਗੰਭੀਰ",
      onset: "ਸ਼ੁਰੂਆਤ",
      location: "ਸਥਾਨ",
      vitalSigns: "ਮਹੱਤਵਪੂਰਨ ਸੰਕੇਤ",
      temp: "ਤਾਪਮਾਨ",
      bp: "BP",
      bpm: "BPM",
      medicalConditions: "ਡਾਕਟਰੀ ਸਥਿਤੀਆਂ",
      currentMedications: "ਮੌਜੂਦਾ ਦਵਾਈਆਂ",
      allergies: "ਐਲਰਜੀਆਂ",
      pregnant: "ਮੌਜੂਦਾ ਸਮੇਂ ਜਾਂ ਸੰਭਵ ਤੌਰ 'ਤੇ ਗਰਭਵਤੀ",
      noVitalsProvided: "ਕੋਈ ਮਹੱਤਵਪੂਰਨ ਸੰਕੇਤ ਜਾਂ ਡਾਕਟਰੀ ਇਤਿਹਾਸ ਪ੍ਰਦਾਨ ਨਹੀਂ ਕੀਤਾ ਗਿਆ",
      noEmergencySigns: "ਕੋਈ ਐਮਰਜੈਂਸੀ ਚੇਤਾਵਨੀ ਸੰਕੇਤ ਰਿਪੋਰਟ ਨਹੀਂ ਕੀਤੇ ਗਏ",
      disclaimer: "ਇਹ ਮੁਲਾਂਕਣ ਤੁਹਾਡੇ ਲੱਛਣਾਂ ਲਈ ਢੁਕਵੇਂ ਪੱਧਰ ਦੀ ਦੇਖਭਾਲ ਬਾਰੇ ਮਾਰਗਦਰਸ਼ਨ ਪ੍ਰਦਾਨ ਕਰੇਗਾ। ਇਹ ਤਸ਼ਖੀਸ ਨਹੀਂ ਹੈ ਅਤੇ ਪੇਸ਼ੇਵਰ ਡਾਕਟਰੀ ਫੈਸਲੇ ਦੀ ਥਾਂ ਨਹੀਂ ਲੈਣੀ ਚਾਹੀਦੀ। ਜੇ ਤੁਸੀਂ ਮਹਿਸੂਸ ਕਰਦੇ ਹੋ ਕਿ ਇਹ ਡਾਕਟਰੀ ਐਮਰਜੈਂਸੀ ਹੈ, ਤਾਂ ਤੁਰੰਤ 911 'ਤੇ ਕਾਲ ਕਰੋ।",
    },
  },
  bn: {
    intro: {
      title: "AI লক্ষণ পরীক্ষক",
      subtitle: "আপনার লক্ষণগুলির উপর ভিত্তি করে ব্যক্তিগত স্বাস্থ্য নির্দেশনা পান",
      howItWorks: "এটি কীভাবে কাজ করে:",
      step1: "আপনার লক্ষণ এবং আপনি কেমন অনুভব করছেন সে সম্পর্কে কয়েকটি প্রশ্নের উত্তর দিন",
      step2: "মূল্যায়ন সম্পূর্ণ করতে মাত্র ২-৩ মিনিট সময় লাগে",
      step3: "আপনার তথ্য সুরক্ষিত এবং HIPAA অনুগত",
      guidance: "আপনি এই বিষয়ে নির্দেশনা পাবেন:",
      homeCare: "বাড়িতে যত্ন",
      seeDoctor: "ডাক্তার দেখান",
      urgentCare: "জরুরি যত্ন",
      emergencyRoom: "জরুরি বিভাগ",
      disclaimer: "এই টুলটি শুধুমাত্র নির্দেশনা প্রদান করে এবং পেশাদার চিকিৎসা পরামর্শের বিকল্প নয়। আপনি যদি চিকিৎসা জরুরি অবস্থায় থাকেন, তাহলে অবিলম্বে 911 এ কল করুন।",
      startAssessment: "মূল্যায়ন শুরু করুন",
      skipIntro: "পরের বার ভূমিকা এড়িয়ে যান",
    },
    wizard: {
      stepTitles: {
        chiefComplaint: "প্রধান অভিযোগ",
        symptomDetails: "লক্ষণের বিবরণ",
        vitalsRisk: "গুরুত্বপূর্ণ সংকেত এবং ঝুঁকির কারণ",
        emergencySymptoms: "জরুরি লক্ষণ",
        review: "পর্যালোচনা",
      },
      stepDescriptions: {
        chiefComplaint: "এই মুহূর্তে আপনাকে সবচেয়ে বেশি কী বিরক্ত করছে তা আমাদের বলুন",
        symptomDetails: "আপনার লক্ষণ, তীব্রতা এবং কখন শুরু হয়েছে তা বর্ণনা করুন",
        vitalsRisk: "যেকোনো প্রাসঙ্গিক গুরুত্বপূর্ণ সংকেত এবং চিকিৎসা ইতিহাস শেয়ার করুন",
        emergencySymptoms: "তাৎক্ষণিক মনোযোগ প্রয়োজন এমন কোনো সতর্কতা সংকেত পরীক্ষা করুন",
        review: "আপনার উত্তরগুলি পর্যালোচনা করুন এবং আপনার ব্যক্তিগত সুপারিশ পান",
      },
      navigation: {
        previous: "পূর্ববর্তী",
        next: "পরবর্তী",
        submit: "সুপারিশ পান",
        saving: "সংরক্ষণ করা হচ্ছে...",
        submitting: "প্রক্রিয়াকরণ হচ্ছে...",
        completeAssessment: "মূল্যায়ন সম্পূর্ণ করুন",
        exit: "প্রস্থান",
      },
      exitDialog: {
        title: "মূল্যায়ন ছেড়ে যাবেন?",
        description: "আপনার স্বাস্থ্য মূল্যায়নে অসংরক্ষিত অগ্রগতি আছে। আপনি কি ছেড়ে যাওয়ার আগে আপনার অগ্রগতি সংরক্ষণ করতে চান?",
        continueAssessment: "মূল্যায়ন চালিয়ে যান",
        leaveWithoutSaving: "সংরক্ষণ না করে ছেড়ে যান",
        saveAndExit: "সংরক্ষণ করুন এবং প্রস্থান করুন",
      },
      header: {
        title: "স্বাস্থ্য মূল্যায়ন",
        stepOf: "ধাপ {current} এর মধ্যে {total}",
      },
    },
    chiefComplaint: {
      title: "আজ আপনি কেন এসেছেন? আপনার প্রধান উদ্বেগ কী?",
      subtitle: "নীচে একটি সাধারণ অভিযোগ নির্বাচন করুন বা আপনার নিজের ভাষায় আপনার উদ্বেগ বর্ণনা করুন।",
      commonComplaints: "সাধারণ অভিযোগ:",
      orDescribe: "অথবা আপনার নিজের ভাষায় বর্ণনা করুন:",
      placeholder: "অনুগ্রহ করে আপনার প্রধান লক্ষণ বা উদ্বেগ বর্ণনা করুন...",
      hint: "যতটা সম্ভব নির্দিষ্ট হন। এটি কখন শুরু হয়েছে এবং কী এটিকে ভালো বা খারাপ করে তা অন্তর্ভুক্ত করুন।",
      complaints: {
        chestPain: "বুকে ব্যথা",
        shortnessOfBreath: "শ্বাসকষ্ট",
        headache: "মাথাব্যথা",
        abdominalPain: "পেটে ব্যথা",
        fever: "জ্বর",
        cough: "কাশি",
        nauseaVomiting: "বমি বমি ভাব/বমি",
        dizziness: "মাথা ঘোরা",
        backPain: "পিঠে ব্যথা",
        fatigue: "ক্লান্তি",
        woundInjury: "ক্ষত/আঘাত",
        rashSkin: "ফুসকুড়ি/ত্বকের সমস্যা",
      },
    },
    symptomDetails: {
      title: "আপনি কোন লক্ষণগুলি অনুভব করছেন? (সমস্ত প্রযোজ্য নির্বাচন করুন)",
      painSeverity: "ব্যথার তীব্রতা (০ = কোনো ব্যথা নেই, ১০ = সবচেয়ে খারাপ সম্ভাব্য ব্যথা)",
      painScale: { noPain: "কোনো ব্যথা নেই", worstPain: "সবচেয়ে খারাপ ব্যথা" },
      painLabels: { noPain: "কোনো ব্যথা নেই", mild: "হালকা ব্যথা", moderate: "মাঝারি ব্যথা", severe: "তীব্র ব্যথা", worst: "সবচেয়ে খারাপ সম্ভাব্য ব্যথা" },
      symptomOnset: "আপনার লক্ষণগুলি কখন শুরু হয়েছিল?",
      onsetPlaceholder: "লক্ষণ কখন শুরু হয়েছে তা বেছে নিন",
      onsetOptions: {
        lessThan1Hour: "১ ঘণ্টারও কম আগে",
        oneToSixHours: "১-৬ ঘণ্টা আগে",
        sixTo24Hours: "৬-২৪ ঘণ্টা আগে",
        oneToThreeDays: "১-৩ দিন আগে",
        threeToSevenDays: "৩-৭ দিন আগে",
        moreThanWeek: "১ সপ্তাহেরও বেশি আগে",
      },
      locationTitle: "আপনি বিশেষভাবে কোথায় লক্ষণ অনুভব করছেন?",
      locationPlaceholder: "যেমন, বাম বুক, নিচের পিঠ, মাথার ডান দিক...",
      symptoms: {
        chestPain: "বুকে ব্যথা",
        shortnessOfBreath: "শ্বাসকষ্ট",
        difficultyBreathing: "শ্বাস নিতে অসুবিধা",
        severeBleeding: "তীব্র রক্তপাত",
        lossOfConsciousness: "চেতনা হারানো",
        confusion: "বিভ্রান্তি",
        speechDifficulty: "কথা বলতে অসুবিধা",
        fever: "জ্বর",
        severeHeadache: "তীব্র মাথাব্যথা",
        abdominalPain: "পেটে ব্যথা",
        nauseaVomiting: "বমি বমি ভাব/বমি",
        dizziness: "মাথা ঘোরা",
        fatigue: "ক্লান্তি",
        backPain: "পিঠে ব্যথা",
        jointPain: "গাঁটে ব্যথা",
      },
    },
    vitalsRisk: {
      prefilledNote: "কিছু ক্ষেত্র আপনার প্রোফাইল থেকে আগে থেকে পূরণ করা হয়েছে।",
      updateProfile: "প্রোফাইল আপডেট করুন",
      vitalsTitle: "গুরুত্বপূর্ণ সংকেত (ঐচ্ছিক - যদি আপনার সাম্প্রতিক পরিমাপ থাকে)",
      temperature: "তাপমাত্রা (°F)",
      bloodPressure: "রক্তচাপ",
      heartRate: "হৃদস্পন্দন (bpm)",
      medicalHistoryTitle: "চিকিৎসা ইতিহাস (সমস্ত প্রযোজ্য নির্বাচন করুন)",
      conditions: {
        heartDisease: "হৃদরোগ",
        hypertension: "উচ্চ রক্তচাপ",
        diabetes: "ডায়াবেটিস",
        asthma: "হাঁপানি",
        copd: "COPD",
        chronicKidneyDisease: "দীর্ঘস্থায়ী কিডনি রোগ",
        liverDisease: "যকৃতের রোগ",
        cancer: "ক্যান্সার",
        immunocompromised: "রোগ প্রতিরোধ ক্ষমতা দুর্বল",
        strokeHistory: "স্ট্রোকের ইতিহাস",
        seizureDisorder: "খিঁচুনি রোগ",
        mentalHealthCondition: "মানসিক স্বাস্থ্য অবস্থা",
      },
      medicationsTitle: "বর্তমান ওষুধ",
      medicationsPlaceholder: "বর্তমান ওষুধগুলি তালিকাভুক্ত করুন, কমা দিয়ে আলাদা করুন",
      medicationsHint: "প্রেসক্রিপশন এবং ওভার-দ্য-কাউন্টার ওষুধ, ভিটামিন এবং সাপ্লিমেন্ট অন্তর্ভুক্ত করুন",
      allergiesTitle: "জানা অ্যালার্জি",
      allergiesPlaceholder: "অ্যালার্জিগুলি তালিকাভুক্ত করুন, কমা দিয়ে আলাদা করুন",
      allergiesHint: "ওষুধের অ্যালার্জি, খাবারের অ্যালার্জি এবং পরিবেশগত অ্যালার্জি অন্তর্ভুক্ত করুন",
      recentVisitsTitle: "সাম্প্রতিক চিকিৎসা পরিদর্শন (গত ২ সপ্তাহ)",
      visits: {
        emergency: "জরুরি বিভাগ",
        urgentCare: "জরুরি যত্ন",
        primaryCare: "প্রাথমিক যত্ন ডাক্তার",
        specialist: "বিশেষজ্ঞ",
        hospitalization: "হাসপাতালে ভর্তি",
      },
      pregnancyLabel: "বর্তমানে গর্ভবতী বা গর্ভবতী হতে পারেন",
    },
    redFlags: {
      title: "জরুরি সতর্কতা সংকেত",
      subtitle: "আপনি বর্তমানে যে লক্ষণগুলি অনুভব করছেন তার যেকোনো একটি চিহ্নিত করুন। এগুলি চিকিৎসা জরুরি অবস্থার ইঙ্গিত দিতে পারে যার জন্য তাৎক্ষণিক মনোযোগ প্রয়োজন।",
      emergencyAlert: "জরুরি: আপনার লক্ষণগুলির উপর ভিত্তি করে, আপনার তাৎক্ষণিক জরুরি যত্ন নেওয়া উচিত। 911 এ কল করুন বা এখনই নিকটতম জরুরি বিভাগে যান।",
      criticalNote: "গুরুতর - তাৎক্ষণিক জরুরি যত্ন প্রয়োজন",
      noEmergencyMessage: "✓ কোনো জরুরি সতর্কতা সংকেত নির্বাচন করা হয়নি। এটি ভালো - আপনার মূল্যায়ন চালিয়ে যান।",
      disclaimer: "গুরুত্বপূর্ণ: আপনি যদি জীবন-ঝুঁকিপূর্ণ জরুরি অবস্থায় থাকেন, তাহলে এই মূল্যায়ন সম্পূর্ণ করার পরিবর্তে অবিলম্বে 911 এ কল করুন। এই টুলটি জরুরি চিকিৎসা যত্নের বিকল্প নয়।",
      symptoms: {
        severeBleeding: "তীব্র রক্তপাত যা বন্ধ হচ্ছে না",
        unconscious: "চেতনা হারানো বা অজ্ঞান হওয়া",
        difficultyBreathingSevere: "শ্বাস নিতে তীব্র অসুবিধা বা শ্বাস নিতে পারছেন না",
        chestPainSevere: "তীব্র বুকে ব্যথা বা চাপ",
        neurological: "হঠাৎ বিভ্রান্তি, কথা জড়িয়ে যাওয়া, বা এক পাশে দুর্বলতা",
        severeInjury: "দুর্ঘটনা বা আঘাত থেকে গুরুতর আঘাত",
        allergicReaction: "তীব্র অ্যালার্জি প্রতিক্রিয়ার লক্ষণ (ফোলা, আমবাত, শ্বাস নিতে সমস্যা)",
        suicidalThoughts: "নিজেকে বা অন্যদের আঘাত করার চিন্তা",
        highFever: "খুব উচ্চ জ্বর (103°F এর বেশি) বিভ্রান্তি সহ",
        severeAbdominalPain: "বমি সহ তীব্র পেটে ব্যথা",
        severeHeadache: "হঠাৎ, তীব্র মাথাব্যথা যা আগে কখনো হয়নি",
        visionChanges: "হঠাৎ দৃষ্টিতে পরিবর্তন বা হারানো",
      },
    },
    review: {
      title: "আপনার মূল্যায়ন পর্যালোচনা করুন",
      subtitle: "অনুগ্রহ করে জমা দেওয়ার আগে সমস্ত তথ্য সঠিক কিনা যাচাই করুন",
      sectionsCompleted: "৪টির মধ্যে বিভাগ সম্পূর্ণ",
      editHint: "সম্পাদনা করতে উপরে সম্পূর্ণ পদক্ষেপে ক্লিক করুন",
      emergencyAlert: "জরুরি সতর্কতা: আপনার লক্ষণগুলির উপর ভিত্তি করে, আপনার তাৎক্ষণিক চিকিৎসা যত্ন নেওয়া উচিত। 911 এ কল করুন বা এখনই নিকটতম জরুরি বিভাগে যান।",
      step1Title: "ধাপ ১: প্রধান উদ্বেগ",
      step2Title: "ধাপ ২: লক্ষণের বিবরণ",
      step3Title: "ধাপ ৩: গুরুত্বপূর্ণ সংকেত এবং চিকিৎসা ইতিহাস",
      step4Title: "ধাপ ৪: জরুরি সতর্কতা সংকেত",
      notSpecified: "নির্দিষ্ট করা হয়নি",
      selectedSymptoms: "নির্বাচিত লক্ষণ",
      noSymptomsSelected: "কোনো লক্ষণ নির্বাচন করা হয়নি",
      painLevel: "ব্যথার মাত্রা",
      mild: "হালকা",
      moderate: "মাঝারি",
      severe: "তীব্র",
      onset: "শুরু",
      location: "অবস্থান",
      vitalSigns: "গুরুত্বপূর্ণ সংকেত",
      temp: "তাপ",
      bp: "BP",
      bpm: "BPM",
      medicalConditions: "চিকিৎসা অবস্থা",
      currentMedications: "বর্তমান ওষুধ",
      allergies: "অ্যালার্জি",
      pregnant: "বর্তমানে বা সম্ভবত গর্ভবতী",
      noVitalsProvided: "কোনো গুরুত্বপূর্ণ সংকেত বা চিকিৎসা ইতিহাস প্রদান করা হয়নি",
      noEmergencySigns: "কোনো জরুরি সতর্কতা সংকেত রিপোর্ট করা হয়নি",
      disclaimer: "এই মূল্যায়ন আপনার লক্ষণগুলির জন্য যথাযথ স্তরের যত্নের বিষয়ে নির্দেশনা প্রদান করবে। এটি রোগ নির্ণয় নয় এবং পেশাদার চিকিৎসা বিচারের বিকল্প হওয়া উচিত নয়। আপনি যদি মনে করেন এটি চিকিৎসা জরুরি অবস্থা, অবিলম্বে 911 এ কল করুন।",
    },
  },
};
